'use client';

// Small settings controls for the voice sections: a switch row and a choice
// row (a radio group), as buttons with ARIA roles so the company form's input
// styles do not apply. 44 px targets; design tokens only. Labels and hints use
// the Settings label and helper styles (DESIGN-SPEC §6.2), like every field.
import { fieldHelpClass, fieldLabelClass } from '@/components/ui/form-control';
import { cn } from '@/lib/utils';
import { useId, type ReactNode } from 'react';

export function SwitchRow({
  label,
  hint,
  on,
  disabled,
  slot,
  onChange,
}: {
  label: string;
  hint?: string;
  on: boolean;
  disabled?: boolean;
  slot: string;
  onChange(next: boolean): void;
}) {
  const labelId = useId();
  const hintId = useId();
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      aria-labelledby={labelId}
      aria-describedby={hint ? hintId : undefined}
      aria-disabled={disabled || undefined}
      data-slot={slot}
      className={cn(
        'flex min-h-11 w-full items-center justify-between gap-4 rounded-lg py-1.5 text-left',
        disabled && 'cursor-not-allowed opacity-60',
      )}
      onClick={() => {
        if (!disabled) onChange(!on);
      }}
    >
      <span className="flex min-w-0 flex-col gap-0.5">
        <span id={labelId} className={fieldLabelClass}>
          {label}
        </span>
        {hint && (
          <span id={hintId} className={fieldHelpClass}>
            {hint}
          </span>
        )}
      </span>
      <span
        aria-hidden="true"
        className={cn(
          'relative inline-flex h-6 w-10 shrink-0 items-center rounded-full border transition-colors',
          on ? 'bg-primary border-primary' : 'bg-muted border-border',
        )}
      >
        <span
          className={cn(
            'bg-background size-4.5 rounded-full shadow-sm transition-transform motion-reduce:transition-none',
            on ? 'translate-x-[1.1rem]' : 'translate-x-0.5',
          )}
        />
      </span>
    </button>
  );
}

export type Choice<T extends string> = {
  value: T;
  label: string;
  hint?: string;
  disabled?: boolean;
};

export function ChoiceRow<T extends string>({
  label,
  value,
  choices,
  slot,
  onChange,
}: {
  label: string;
  value: T;
  choices: Choice<T>[];
  slot: string;
  onChange(next: T): void;
}) {
  const labelId = useId();
  return (
    <div className="flex flex-col gap-2" data-slot={slot}>
      <span id={labelId} className={fieldLabelClass}>
        {label}
      </span>
      <div role="radiogroup" aria-labelledby={labelId} className="flex flex-col gap-1.5">
        {choices.map((choice) => (
          <button
            key={choice.value}
            type="button"
            role="radio"
            aria-checked={choice.value === value}
            aria-disabled={choice.disabled || undefined}
            data-value={choice.value}
            className={cn(
              'border-border/80 flex min-h-11 w-full flex-col items-start gap-0.5 rounded-lg border px-3 py-2 text-left text-sm',
              choice.value === value && 'border-primary bg-accent',
              choice.disabled && 'cursor-not-allowed opacity-60',
            )}
            onClick={() => {
              if (!choice.disabled && choice.value !== value) onChange(choice.value);
            }}
          >
            <span className="font-medium">{choice.label}</span>
            {choice.hint && <span className={fieldHelpClass}>{choice.hint}</span>}
          </button>
        ))}
      </div>
    </div>
  );
}

export function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-2">
      <span className={fieldLabelClass}>{label}</span>
      {children}
    </div>
  );
}
